import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

import { useNavigate } from "react-router-dom";
import { MdEmail, MdLock } from "react-icons/md";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import Header from "../Header";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../firebase";

export default function Login() {
  const [identifier, setIdentifier] = useState(""); // email or phone
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const getFriendlyError = (code) => {
    switch (code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
        return "Incorrect username or password.";
      case "auth/user-not-found":
        return "No account found with this email.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please try again later.";
      default:
        return "Something went wrong. Please try again.";
    }
  };

  // 🔹 Save user in localStorage
  const saveUserToLocalStorage = (data) => {
    localStorage.setItem(
      "user",
      JSON.stringify({
        uid: data.uid,
        name: data.name || "User",
        role: data.role || "patient",
        email: data.email || "",
      })
    );
  };

  // 🔹 Google Login
  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userDoc = await getDoc(doc(db, "users", user.uid));
      let role = "patient";
      let name = user.displayName || "User";

      if (userDoc.exists()) {
        role = userDoc.data().role || "patient";
        name = userDoc.data().name || name;
      }

      saveUserToLocalStorage({
        uid: user.uid,
        name,
        role,
        email: user.email,
      });

      if (role === "admin") navigate("/admin-dashboard");
      else if (role === "doctor") navigate("/doctor-dashboard");
      else navigate("/"); // patient
    } catch (err) {
      console.error(err);
      setError(getFriendlyError(err.code));
    }
  };

  // 🔹 Guest Login
  const handleGuestLogin = async () => {
    try {
      const result = await signInAnonymously(auth);
      console.log("Guest User:", result.user);

      saveUserToLocalStorage({
        uid: result.user.uid,
        name: "Guest",
        role: "guest",
        email: "",
      });

      navigate("/");
    } catch (error) {
      console.error("Guest Login Error:", error);
    }
  };

  // 🔹 Email / Phone Login
  const handleAccess = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (!identifier || !password) {
      setError("Please enter both username/phone and password.");
      return;
    }

    try {
      const isEmail = /\S+@\S+\.\S+/.test(identifier);
      let role = "";
      let name = "";
      let uid = "";
      let email = "";
      let firstLogin = false;
      let userDocRef = null;

      if (isEmail) {
        // ✅ Login with email + password
        const userCredential = await signInWithEmailAndPassword(
          auth,
          identifier,
          password
        );
        const user = userCredential.user;

        userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          setError("User profile not found.");
          return;
        }

        const userData = userDoc.data();
        role = userData.role;
        name = userData.name || "User";
        uid = user.uid;
        email = user.email;
        firstLogin = userData.firstLogin || false;
      } else {
        // ✅ Login with phone
        const q = query(
          collection(db, "users"),
          where("phone", "==", identifier)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setError("Please enter a valid email address or phone number.");
          return;
        }

        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();

        if (userData.password !== password) {
          setError("Invalid phone/password.");
          return;
        }

        role = userData.role;
        name = userData.name || "User";
        uid = userDoc.id;
        email = userData.email || "";
        firstLogin = userData.firstLogin || false;

        userDocRef = doc(db, "users", uid);
      }

      // ✅ Handle first login email
      if (firstLogin && email) {
        const [firstName, lastName = ""] = name.split(" ", 2);

        try {
          await fetch("https://vpjc-api-805872224468.asia-south1.run.app/api/send-first-login-email", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email,
              first_name: firstName,
              last_name: lastName,
            }),
          });

          console.log("✅ First login email sent");

          // 🔹 Mark firstLogin = false
          if (userDocRef) {
            await updateDoc(userDocRef, { firstLogin: false });
          }
        } catch (err) {
          console.error("❌ Error sending first login email:", err);
        }
      }

      // ✅ Save logged-in user in local storage
      saveUserToLocalStorage({ uid, name, role, email });

      // 🔹 Redirect based on role
      if (role === "admin") navigate("/admin-dashboard");
      else if (role === "doctor") navigate("/doctor-dashboard");
      else if (role === "patient") navigate("/patient-dashboard");
      else setError("Your account role is not recognized.");
    } catch (err) {
      console.error(err);
      setError(getFriendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen flex items-center justify-center px-4 py-16 bg-gradient-to-br from-white via-blue-50 to-slate-50">
        <div className="w-full max-w-4xl shadow-xl rounded-2xl overflow-hidden flex flex-col md:flex-row bg-white">
          {/* Illustration */}
          <div className="md:w-1/2 flex items-center justify-center p-6 bg-white">
            <img
              src="./LoginIllstn.jpg"
              alt="Illustration"
              className="w-4/5 h-auto object-contain"
            />
          </div>

          {/* Form */}
          <div className="md:w-1/2 p-8 flex flex-col justify-center">
            <h2 className="text-3xl font-bold text-[#204080] mb-6 text-center">
              Sign In
            </h2>

            {error && (
              <div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg mb-5 text-sm text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleAccess} className="space-y-5">
              {/* Email / Phone */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                  <MdEmail />
                </span>
                <input
                  type="text"
                  placeholder="Enter Email or Phone Number"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#204080] bg-gray-50 text-gray-800 placeholder-gray-400 transition-all"
                />
              </div>

              {/* Password */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                  <MdLock />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Your Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-10 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#204080] bg-gray-50 text-gray-800 placeholder-gray-400 transition-all"
                />
                {password.length > 0 && (
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-base cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </span>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-lg font-semibold transition-all ${
                  loading
                    ? "bg-gray-300 cursor-not-allowed text-gray-600"
                    : "bg-[#204080] text-white hover:bg-blue-900"
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      />
                    </svg>
                    <span>Logging in...</span>
                  </div>
                ) : (
                  "Login"
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center my-5">
              <hr className="flex-grow border-gray-300" />
              <span className="mx-2 text-gray-500 text-sm">OR</span>
              <hr className="flex-grow border-gray-300" />
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleGoogleSignIn}
                className="w-1/2 py-3 rounded-lg border border-gray-300 flex items-center justify-center gap-3 hover:bg-gray-50 transition-all"
              >
                <FcGoogle className="text-2xl" /> Sign in with Google
              </button>

              <button
                onClick={handleGuestLogin}
                className="w-1/2 py-3 rounded-lg border border-gray-300 flex items-center justify-center gap-3 hover:bg-gray-50 transition-all"
              >
                Continue as Guest
              </button>
            </div>

            <p className="text-center text-gray-600 text-sm mt-6">
              Don’t have an account?{" "}
              <span
                onClick={() => navigate("/signup")}
                className="text-[#204080] font-semibold hover:underline cursor-pointer"
              >
                Create an account
              </span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
